import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchBlogPost } from "@/lib/api";
import { ArrowLeft, Clock, ArrowUpRight } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const catLabel = { lean: "Lean", neuro: "Neuro", ia: "IA", cas: "Étude de cas" };

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useDocumentTitle(post?.title || "Article", post?.excerpt);

  useEffect(() => {
    setLoading(true);
    fetchBlogPost(slug)
      .then(setPost)
      .catch(() => navigate("/blog"))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) {
    return (
      <main className="pt-32 pb-24 bg-pearl min-h-screen">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 text-mute" data-testid="blog-post-loading">Chargement…</div>
      </main>
    );
  }
  if (!post) return null;

  return (
    <main className="pt-28 pb-24 bg-pearl min-h-screen" data-testid="blog-post-page">
      <article className="max-w-3xl mx-auto px-6 sm:px-10">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-mute hover:text-ink transition mb-10" data-testid="blog-post-back">
          <ArrowLeft size={14} /> Tous les articles
        </Link>

        <div className="flex items-center gap-3 mb-6 text-xs">
          <span className="font-mono-overline text-mute">{catLabel[post.category] || post.category}</span>
          <span className="text-mute">·</span>
          <span className="inline-flex items-center gap-1 text-mute">
            <Clock size={12} /> {post.read_minutes} min
          </span>
        </div>

        <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-6" data-testid="blog-post-title">
          {post.title}
        </h1>
        <p className="text-mute text-xl leading-relaxed mb-10" data-testid="blog-post-excerpt">{post.excerpt}</p>

        <div className="aspect-[16/9] overflow-hidden bg-pearl-dark mb-12">
          <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <div className="text-lg leading-[1.8] text-ink space-y-6 mb-16" data-testid="blog-post-body">
          {post.body.split("\n\n").map((block, i) => {
            const trimmed = block.trim();
            if (trimmed.startsWith("## ")) {
              return (
                <h2 key={i} className="font-serif text-3xl sm:text-4xl mt-12 mb-2 leading-tight">
                  {trimmed.slice(3)}
                </h2>
              );
            }
            if (trimmed.startsWith("> ")) {
              return (
                <blockquote key={i} className="border-l-2 border-gold pl-6 italic text-mute my-6 font-serif text-xl leading-snug">
                  {trimmed.slice(2)}
                </blockquote>
              );
            }
            if (trimmed.startsWith("- ")) {
              const items = trimmed.split("\n").map((l) => l.replace(/^- /, ""));
              return (
                <ul key={i} className="list-disc pl-6 space-y-2 marker:text-gold">
                  {items.map((it, j) => <li key={j}>{it}</li>)}
                </ul>
              );
            }
            return <p key={i}>{trimmed}</p>;
          })}
        </div>

        <div className="border-t border-ink/10 pt-12">
          <div className="font-mono-overline text-mute mb-3">Et toi ?</div>
          <h3 className="font-serif text-3xl mb-6 leading-tight">
            Tu reconnais ton organisation dans cet article ?
          </h3>
          <Link to="/#diagnostic" className="btn-primary" data-testid="blog-post-cta">
            Réserver un diagnostic gratuit <ArrowUpRight size={16} />
          </Link>
        </div>
      </article>
    </main>
  );
}
